import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Tooltip } from 'reactstrap';
import Icon from 'utility/icomoon';

interface Props {
  // Estado controlado: true = modo multiselección activo.
  active: boolean;
  // Se invoca con el próximo valor al hacer clic en el toggle.
  onToggle: (next: boolean) => void;
  // Acciones masivas. Solo se muestran si se proveen y el modo está activo.
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  // Conteo actual para el badge (opcional).
  selectedCount?: number;
  id?: string;
  isDisabled?: boolean;
  className?: string;
  iconSize?: number;
  tooltipPosition?: string;
}

// Botón global para catálogos: alterna el modo multiselección y, cuando está
// activo, expone seleccionar/deseleccionar todos + conteo. Se ubica a la
// izquierda del toolbar (slot buttonFilter). Presentacional: el estado y los
// triggers de la tabla viven en la pantalla que lo usa.
const MultiSelectButton = (props: Props) => {
  const {
    active,
    onToggle,
    onSelectAll,
    onDeselectAll,
    selectedCount = 0,
    id = 'multiSelectButton',
    isDisabled = false,
    className = '',
    iconSize = 16,
    tooltipPosition = 'top'
  } = props;

  const intl = useIntl();
  const [toggleTooltip, setToggleTooltip] = useState(false);
  const [selectTooltip, setSelectTooltip] = useState(false);
  const [deselectTooltip, setDeselectTooltip] = useState(false);

  const toggleMessage = intl.formatMessage({
    id: active ? 'common.multiSelectDisable' : 'common.multiSelectEnable'
  });

  return (
    <div className="d-flex align-items-center multiselect-toolbar">
      <Button.Ripple
        id={id}
        className={`btn-icon ${isDisabled ? 'disabled' : ''} ${className}`}
        color={active ? 'primary' : 'secondary'}
        aria-pressed={active}
        disabled={isDisabled}
        onClick={() => onToggle(!active)}
      >
        <Icon size={iconSize} icon="multiple" />
      </Button.Ripple>
      <Tooltip
        placement={tooltipPosition}
        isOpen={toggleTooltip}
        target={id}
        toggle={() => setToggleTooltip(!toggleTooltip)}
        trigger="click hover"
      >
        {toggleMessage}
      </Tooltip>

      {active && onSelectAll && (
        <>
          <Button.Ripple
            id={`${id}SelectAll`}
            className="btn-icon ml-1"
            color="secondary"
            disabled={isDisabled}
            onClick={onSelectAll}
          >
            <Icon size={iconSize} icon="CheckSquare" />
          </Button.Ripple>
          <Tooltip
            placement={tooltipPosition}
            isOpen={selectTooltip}
            target={`${id}SelectAll`}
            toggle={() => setSelectTooltip(!selectTooltip)}
            trigger="click hover"
          >
            {intl.formatMessage({ id: 'common.selectAll' })}
          </Tooltip>
        </>
      )}

      {active && onDeselectAll && (
        <>
          <Button.Ripple
            id={`${id}DeselectAll`}
            className="btn-icon ml-1"
            color="secondary"
            disabled={isDisabled}
            onClick={onDeselectAll}
          >
            <Icon size={iconSize} icon="Square" />
          </Button.Ripple>
          <Tooltip
            placement={tooltipPosition}
            isOpen={deselectTooltip}
            target={`${id}DeselectAll`}
            toggle={() => setDeselectTooltip(!deselectTooltip)}
            trigger="click hover"
          >
            {intl.formatMessage({ id: 'common.deselectAll' })}
          </Tooltip>
        </>
      )}

      {active && selectedCount > 0 && (
        <span className="multiselect-count ml-1">
          {intl.formatMessage({ id: 'common.itemsSelected' }, { count: selectedCount })}
        </span>
      )}
    </div>
  );
};

export default MultiSelectButton;
